import { useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader } from "@/components/app/common";
import { DataTable } from "@/components/app/DataTable";
import { useMe } from "@/components/app/MeContext";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";

export const TERM_KINDS = [["payment", "Payment terms"], ["delivery", "Delivery terms / lead time"], ["warranty", "Warranty terms"], ["template", "T&C templates"]];

function TermDialog({ kind, term, onClose, onSaved }) {
  const [f, setF] = useState(term || { code: "", name: "", description: "" });
  const tpl = kind === "template";
  const save = async () => {
    try {
      const body = { kind, code: f.code, name: f.name, description: f.description || "" };
      term ? await api.put(`/sales-terms/${term.id}`, body) : await api.post("/sales-terms", body);
      toast.success("Saved"); onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl" data-testid="term-dialog">
        <DialogHeader><DialogTitle>{term ? "Edit" : "New"} {TERM_KINDS.find((k) => k[0] === kind)[1].toLowerCase()}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-3 gap-3">
          <div><Label>{tpl ? "Template code" : "Term code"} *</Label><Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} data-testid="term-code" /></div>
          <div className="col-span-2"><Label>{tpl ? "Template name" : "Term name"} *</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="term-name" /></div>
          <div className="col-span-3"><Label>{tpl ? "Template text (printed on the quotation)" : "Description"}</Label>
            <Textarea rows={tpl ? 10 : 3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="term-description" /></div>
        </div>
        <DialogFooter><Button onClick={save} disabled={!f.code.trim() || !f.name.trim()} data-testid="term-save-btn">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function SalesTerms() {
  const { can } = useMe();
  const [kind, setKind] = useState("payment");
  const { data, reload } = useFetch(`/sales-terms?kind=${kind}`);
  const [edit, setEdit] = useState(null);
  const toggle = async (t, v) => { try { await api.post(`/sales-terms/${t.id}/status`, { active: v }); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const cols = [
    { key: "code", label: "Code", mono: true },
    { key: "name", label: "Name", wrap: true },
    { key: "description", label: kind === "template" ? "Template text" : "Description", wrap: true, render: (t) => <span className="line-clamp-2 text-slate-600">{t.description}</span> },
    { key: "active", label: "Active", render: (t) => <span onClick={(e) => e.stopPropagation()}><Switch checked={t.active} disabled={!can("sales.delete")} onCheckedChange={(v) => toggle(t, v)} data-testid={`term-active-${t.code}`} /></span> },
  ];
  return (
    <div data-testid="sales-terms-page">
      <PageHeader title="Terms & Conditions masters" subtitle="Pick these on quotations instead of typing them each time. Inactive terms stay on old quotations.">
        {can("sales.create") && <Button onClick={() => setEdit({})} data-testid="term-new-btn"><Plus className="h-4 w-4 mr-1" />New</Button>}
      </PageHeader>
      <Tabs value={kind} onValueChange={setKind} className="mb-4">
        <TabsList className="flex-wrap h-auto">{TERM_KINDS.map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`terms-tab-${k}`}>{l}</TabsTrigger>)}</TabsList>
      </Tabs>
      <DataTable testid="terms-table" columns={cols} rows={data} onRowClick={can("sales.edit") ? (t) => setEdit(t) : undefined} empty="None yet — add the ones you use" />
      {edit && <TermDialog key={edit.id || "new"} kind={kind} term={edit.id ? edit : null} onClose={() => setEdit(null)} onSaved={reload} />}
    </div>
  );
}
